import type { ReactNode } from 'react';
import type { ActivityEvent } from '../organisms';

/**
 * activityFeed — the social-feed phrasing shared by the staff dashboard's
 * Recent-activity panel and the client-portal Activity tab, so both feeds read
 * identically ("Sarah added a parcel") rather than the raw operator field-diff
 * line ("… : field changed"). Extracted from Dashboard.tsx so the portal can
 * mirror the exact staff phrasing without duplicating it.
 */

/**
 * Feed-style phrasing for an activity line — reads like a social feed
 * ("Sarah updated a tax bill") rather than the raw "tax_bill · update".
 * The actor is always a person's name (or 'System'); it is NEVER an email
 * address (the caller passes the already-resolved actor name).
 */
export function activityTitle(entityType: string, action: string, actor?: string | null): ReactNode {
  const who = <strong>{actor || 'System'}</strong>;
  const thing = (entityType || '').replace(/_/g, ' ');
  const article = /^[aeiou]/i.test(thing) ? 'an' : 'a';
  switch (action) {
    case 'create': return <>{who} added {article} {thing}</>;
    case 'update': return <>{who} updated {article} {thing}</>;
    case 'delete': return <>{who} removed {article} {thing}</>;
    case 'bulk_update': return <>{who} bulk-updated {thing} records</>;
    case 'year_roll': return <>{who} ran a year roll</>;
    case 'import_accept': return <>{who} accepted an import</>;
    case 'import_reject': return <>{who} rejected an import</>;
    case 'login_success': return <>{who} signed in</>;
    case 'login_failure': return <>{who} failed to sign in</>;
    case 'logout': return <>{who} signed out</>;
    default: return <>{who} · {(action || '').replace(/_/g, ' ')} {thing}</>;
  }
}

/**
 * Semantic kind for an activity action, driving the ActivityPanel dot color.
 * Mirrors the inline mapping the staff dashboard's snapshot events use
 * (create → success, delete → danger, else info).
 */
export function kindForAction(action: string): NonNullable<ActivityEvent['kind']> {
  if (action === 'create') return 'success';
  if (action === 'delete') return 'danger';
  return 'info';
}
